import React, {useState} from "react"
// MUI
import {Box, Typography, Chip, Button, Stack, Tooltip, IconButton, Menu, MenuItem, ListItemIcon, ListItemText} from "@mui/material"
import {EditOutlined, PhoneAndroidRounded, LocalOfferOutlined, VisibilityOutlined, MoreVert, DeleteOutlined} from "@mui/icons-material"
// Layout / tokens
import {Badge} from "@/components/Layout"
import MoreChip, {MAX_VISIBLE_CHIPS} from "@/components/UI/MoreChip/MoreChip"
import {packagingTokens as tk} from "@/theme/packagingTokens"
import {languageCodeToName} from "@/utils"

type AppCardProps = {
    app: any
    disableActions?: boolean
    onEdit: (app: any) => void
    onAssociatedBrands: (app: any) => void
    onPreview: (app: any) => void
    onDelete: (app: any) => void
    t: (k: string, o?: any) => string
}

export default function AppCard(props: AppCardProps) {
    const {app, disableActions, onEdit, onAssociatedBrands, onPreview, onDelete, t} = props
    const brands = app.brands ?? []
    const languages = app.languages ?? []
    // Both chip lists collapse their tail into a MoreChip past the cap.
    const visibleBrands = brands.slice(0, MAX_VISIBLE_CHIPS)
    const hiddenBrands = brands.slice(MAX_VISIBLE_CHIPS)
    const visibleLanguages = languages.slice(0, MAX_VISIBLE_CHIPS)
    const hiddenLanguages = languages.slice(MAX_VISIBLE_CHIPS)

    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)

    return (
        <Box
            sx={{
                position: "relative",
                backgroundColor: tk.color.surface,
                border: `1px solid ${tk.color.border}`,
                borderRadius: tk.radius.card,
                p: 2.5,
                display: "flex",
                flexDirection: "column",
                transition: "all .2s",
                "&:hover": {boxShadow: tk.shadow.md, borderColor: tk.color.borderStrong},
            }}
        >
            {/* Unnamed marker — gentle hint, not an alert */}
            {!app.name && (
                <Tooltip title={t("unnamed_app")} arrow placement={"top"}>
                    <Box
                        aria-label={t("unnamed_app")}
                        sx={{
                            position: "absolute", top: 10, right: 10,
                            width: 8, height: 8, borderRadius: "50%",
                            backgroundColor: tk.color.amber,
                            boxShadow: `0 0 0 3px ${tk.color.amber100}`,
                        }}
                    />
                </Tooltip>
            )}

            {/* Header */}
            <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1.5}}>
                <Box sx={{display: "flex", alignItems: "center", gap: 1.25, minWidth: 0}}>
                    <Box
                        sx={(theme) => ({
                            width: 42, height: 42, borderRadius: tk.radius.md,
                            backgroundColor: tk.color.green100, color: tk.color.greenDark,
                            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                        })}
                    >
                        <PhoneAndroidRounded sx={{fontSize: 22}} />
                    </Box>
                    {app.name && (
                        <Typography sx={{fontSize: "14px", fontWeight: 600, color: tk.color.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
                            {app.name}
                        </Typography>
                    )}
                </Box>
                {app.default && <Badge tone={"green"} dot>{t("default")}</Badge>}
            </Box>

            {/* Brands */}
            <Typography sx={{fontSize: "11px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: tk.color.textTertiary, mb: 0.75}}>
                {t("brands")}
            </Typography>
            <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1.5}}>
                {brands.length === 0 ? (
                    <Typography sx={{fontSize: "12.5px", color: tk.color.textTertiary, fontStyle: "italic"}}>
                        {t("no_brands_for_app")}
                    </Typography>
                ) : (
                    <>
                        {visibleBrands.map((b: any, i: number) => (
                            <Chip key={i} label={b.name} size={"small"}
                                  sx={{height: 24, borderRadius: tk.radius.pill, backgroundColor: tk.color.bg, border: `1px solid ${tk.color.borderLight}`, fontSize: "11.5px", fontWeight: 500, color: tk.color.textSecondary}} />
                        ))}
                        <MoreChip labels={hiddenBrands.map((b: any) => b.name)} />
                    </>
                )}
            </Box>

            {/* Translations */}
            <Typography sx={{fontSize: "11px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: tk.color.textTertiary, mb: 0.75}}>
                {t("app_translations")}
            </Typography>
            <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.75}}>
                {languages.length === 0 ? (
                    <Typography sx={{fontSize: "12.5px", color: tk.color.textTertiary, fontStyle: "italic"}}>—</Typography>
                ) : (
                    <>
                        {visibleLanguages.map((l: any, i: number) => (
                            <Chip key={i} label={languageCodeToName(l.name)} size={"small"}
                                  sx={{height: 24, borderRadius: tk.radius.pill, backgroundColor: tk.color.green50, border: `1px solid ${tk.color.green100}`, fontSize: "11.5px", fontWeight: 500, color: tk.color.greenDark}} />
                        ))}
                        <MoreChip labels={hiddenLanguages.map((l: any) => languageCodeToName(l.name))} />
                    </>
                )}
            </Box>

            {/* Actions — two quiet rows: primary pair on top, secondary line below */}
            <Box sx={{mt: 2, pt: 1.75, borderTop: `1px solid ${tk.color.borderLight}`}}>
                <Stack direction={"row"} spacing={1}>
                    <Button
                        fullWidth
                        size={"small"} disabled={disableActions} onClick={() => onEdit(app)}
                        startIcon={<EditOutlined sx={{fontSize: 15}} />}
                        sx={{textTransform: "none", fontSize: "12.5px", fontWeight: 600, color: tk.color.textPrimary, border: `1px solid ${tk.color.border}`, borderRadius: tk.radius.sm, "&:hover": {backgroundColor: tk.color.bg, borderColor: tk.color.borderStrong}}}
                    >
                        {t("common:edit")}
                    </Button>
                    <Button
                        fullWidth
                        size={"small"} onClick={() => onPreview(app)}
                        aria-label={t("app_preview")}
                        startIcon={<VisibilityOutlined sx={{fontSize: 15}} />}
                        sx={{textTransform: "none", fontSize: "12.5px", fontWeight: 600, color: tk.color.textPrimary, border: `1px solid ${tk.color.border}`, borderRadius: tk.radius.sm, "&:hover": {backgroundColor: tk.color.bg, borderColor: tk.color.borderStrong}}}
                    >
                        {t("app_preview")}
                    </Button>
                </Stack>
                <Stack direction={"row"} spacing={1} alignItems={"center"} sx={{mt: 1}}>
                    <Button
                        size={"small"} disabled={disableActions} onClick={() => onAssociatedBrands(app)}
                        startIcon={<LocalOfferOutlined sx={{fontSize: 15}} />}
                        sx={{textTransform: "none", fontSize: "12.5px", fontWeight: 500, color: tk.color.textSecondary, borderRadius: tk.radius.sm, px: 1, "&:hover": {backgroundColor: tk.color.bg}}}
                    >
                        {t("associated_brands")}
                    </Button>
                    <Box sx={{flex: 1}} />
                    <IconButton
                        size={"small"} disabled={disableActions}
                        onClick={(e) => setMenuAnchor(e.currentTarget)}
                        aria-label={t("common:more_actions", {defaultValue: "More actions"})}
                        sx={{color: tk.color.textTertiary, "&:hover": {backgroundColor: tk.color.bg}}}
                    >
                        <MoreVert fontSize={"small"} />
                    </IconButton>
                </Stack>
                <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)} MenuListProps={{dense: true}}>
                    {/* The default app cannot be deleted (brand creation auto-associates it). */}
                    <Tooltip title={app.default ? t("app_delete_default_locked") : ""} arrow placement={"left"}>
                        <span>
                            <MenuItem
                                disabled={Boolean(app.default)}
                                onClick={app.default ? undefined : () => {onDelete(app); setMenuAnchor(null)}}
                                sx={{color: tk.color.red}}
                            >
                                <ListItemIcon><DeleteOutlined fontSize={"small"} sx={{color: tk.color.red}} /></ListItemIcon>
                                <ListItemText>{t("common:delete")}</ListItemText>
                            </MenuItem>
                        </span>
                    </Tooltip>
                </Menu>
            </Box>
        </Box>
    )
}
